import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { getOAuthAvatarUrl, getOptimizedAvatarUrl } from '../lib/user-avatar';
import { motion, AnimatePresence } from '../lib/motion-utils';
import {
  Home, 
  Activity, 
  TrendingUp, 
  Settings, 
  Menu, 
  X, 
  User,
  Crown,
  Package,
  Bell,
  Heart,
  BarChart3,
  Calendar,
  MessageSquare,
  UserCircle,
  LogOut,
  Mail,
  FlaskConical,
  Brain,
  Clock3,
  MessagesSquare,
  ArrowLeft,
  Building2,
  Sparkles,
  ShieldCheck,
  Briefcase,
  BookOpen,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import { Backdrop } from '../lib/motion-utils';
import { PremiumValueIndicator } from './PremiumValueIndicator';
import { getUserProfileCached } from '../lib/user-profile-cache';
import { TensionLogo } from './TensionLogo';

interface NavigationProps {
  variant?: 'mobile' | 'desktop' | 'both';
}

type MainNavItem = {
  path: string;
  label: string;
  icon: LucideIcon;
  premium?: boolean;
  highlight?: boolean;
};

function NavUserAvatar({
  imageUrl,
  size,
}: {
  imageUrl: string | null;
  size: 'sm' | 'md';
}) {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [imageUrl]);

  const box = size === 'md' ? 'h-12 w-12' : 'h-8 w-8';
  const iconClass = size === 'md' ? 'h-6 w-6' : 'h-4 w-4';
  const showImg = Boolean(imageUrl && !imgError);

  if (showImg) {
    return (
      <img
        src={imageUrl!}
        alt=""
        width={size === 'md' ? 48 : 32}
        height={size === 'md' ? 48 : 32}
        className={`${box} shrink-0 rounded-full object-cover ring-2 ring-neutral-200 dark:ring-neutral-600`}
        onError={() => setImgError(true)}
        decoding="async"
      />
    );
  }
  return (
    <div
      className={`${box} flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-purple-500`}
    >
      <User className={`${iconClass} text-white`} aria-hidden />
    </div>
  );
}

export function Navigation({ variant = 'both' }: NavigationProps) {
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [appMode, setAppMode] = useState<'relationship' | 'single'>(() =>
    localStorage.getItem('tension_mode') === 'single' ? 'single' : 'relationship',
  );
  
  // Safe hook usage - handle case where component is rendered outside Router context
  let navigate: any;
  let location: any;
  try {
    navigate = useNavigate();
    location = useLocation();
  } catch (error) {
    // If hooks fail (e.g., in Figma Make preview), return null
    console.debug('Navigation: Router hooks failed, hiding navigation', error);
    return null;
  }
  
  const auth = useAuth();

  // Defensive: If auth context is not available during hot reload, use defaults
  const user = auth?.user || null;
  const accessToken = auth?.accessToken ?? null;
  const signOut = auth?.signOut;

  useEffect(() => {
    if (!user) {
      setProfilePhotoUrl(null);
      return;
    }
    setProfilePhotoUrl(getOptimizedAvatarUrl(getOAuthAvatarUrl(user), 'md'));
    if (!accessToken) return;

    let cancelled = false;
    void (async () => {
      try {
        const data = await getUserProfileCached(accessToken);
        if (cancelled) return;
        const url = data?.profile?.photo_url;
        if (typeof url === 'string' && url.trim()) {
          setProfilePhotoUrl(getOptimizedAvatarUrl(url.trim(), 'md'));
        }
      } catch {
        /* keep OAuth fallback */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, accessToken]);

  useEffect(() => {
    const syncMode = () => setAppMode(localStorage.getItem('tension_mode') === 'single' ? 'single' : 'relationship');
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'tension_mode') syncMode();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', syncMode);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', syncMode);
    };
  }, []);

  const handleSignOutFromMenu = async () => {
    if (!signOut) return;
    setSideMenuOpen(false);
    try {
      await signOut();
      toast.success('Signed out');
      navigate('/auth', { replace: true });
    } catch (e) {
      console.error('Sign out error:', e);
      toast.error('Could not sign out. Please try again.');
    }
  };

  // Get subscription status and mode
  const subscription = localStorage.getItem('tension_subscription') || 'free';
  const isPremium = subscription !== 'free';
  const isEnterpriseRoute = location.pathname.startsWith('/enterprise');

  const enterpriseMainNavItems: MainNavItem[] = [
    { path: '/enterprise', label: 'Overview', icon: Home },
    { path: '/enterprise/solutions', label: 'Solutions', icon: Building2 },
    { path: '/enterprise/security', label: 'Security', icon: ShieldCheck },
    { path: '/enterprise/pilot', label: 'Pilot', icon: Activity },
    { path: '/enterprise/platform', label: 'Platform', icon: BarChart3 },
  ];

  const enterpriseSecondaryNavItems: MainNavItem[] = [
    { path: '/enterprise/identity', label: 'Identity', icon: UserCircle },
    { path: '/enterprise/whitelabel', label: 'White-label', icon: Sparkles },
    { path: '/enterprise/partner-docs', label: 'Partner Docs', icon: BookOpen },
    { path: '/enterprise/expansion', label: 'Expansion', icon: Briefcase },
    { path: '/business-inquiries?inquiryType=enterprise', label: 'Contact Sales', icon: Mail, highlight: true },
    { path: '/enterprise/admin', label: 'Enterprise Backend', icon: Settings },
  ];

  // Main navigation items - conditional based on mode
  const consumerMainNavItems: MainNavItem[] = [
    { path: '/home', label: 'Home', icon: Home },
    { path: '/check-in', label: 'Check-In', icon: Activity },
    { path: '/timeline', label: 'Timeline', icon: Clock3 },
    { path: '/insights', label: 'Insights', icon: BarChart3, premium: true },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  // Secondary navigation items (for drawer) - conditional based on mode
  const consumerSecondaryNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: TrendingUp },
    { path: '/relationships', label: 'Relationships', icon: Heart },
    ...(appMode === 'relationship' ? [{ path: '/home', label: 'Intelligence', icon: Brain }] : []),
    { path: '/packs', label: 'Situational Packs', icon: Package },
    { path: '/summary', label: 'Weekly Summary', icon: Calendar },
    { path: '/contact', label: 'Contact', icon: Mail },
    { path: '/notifications', label: 'Notifications', icon: Bell },
    { path: '/pricing', label: 'Upgrade', icon: Crown, highlight: !isPremium },
  ];

  // Community navigation items
  const communityNavItems = [
    { path: '/social-coming-soon', label: 'Social Media (Coming Soon)', icon: MessagesSquare },
  ];

  const mainNavItems = isEnterpriseRoute ? enterpriseMainNavItems : consumerMainNavItems;
  const secondaryNavItems = isEnterpriseRoute ? enterpriseSecondaryNavItems : consumerSecondaryNavItems;

  const isActive = (path: string) => location.pathname === path;

  /** One-tap history back; hidden on home so “Back” doesn’t fight the primary hub. */
  const showAppBack = Boolean(user) && location.pathname !== '/home';

  const handleNavigate = (path: string) => {
    setSideMenuOpen(false);
    if (path.startsWith('/')) {
      navigate(path);
      return;
    }
    navigate(path);
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSideMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Ensure we never render objects (React #130) - coerce to string
  const safeStr = (v: unknown): string =>
    v != null && typeof v === 'string' ? v : '';
  const displayName = safeStr(typeof user?.user_metadata?.name === 'string' ? user?.user_metadata?.name : 'User');
  const displayPlan = isEnterpriseRoute
    ? 'Enterprise'
    : safeStr(typeof subscription === 'string' ? subscription : 'free');

  // Top Header with TENSION branding and hamburger menu
  const TopHeader = () => (
    <motion.header
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/98 shadow-[0_10px_24px_rgba(15,23,42,0.08)] backdrop-blur-[6px]"
      role="banner"
    >
      <div className="mx-auto flex h-16 w-full max-w-[1200px] items-center justify-between px-4 sm:h-20 sm:px-6">
        <div className="flex min-w-0 shrink-0 items-center gap-0.5 sm:gap-1">
          {showAppBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-[14px] px-2 text-slate-700 transition-all duration-base ease-out hover:bg-slate-100 sm:px-2.5"
              aria-label="Go back to previous page"
              title="Back"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" aria-hidden />
              <span className="hidden text-sm font-medium sm:inline">Back</span>
            </button>
          )}
          {/* Hamburger Menu Button */}
          <button
            onClick={() => setSideMenuOpen(true)}
            className="flex min-h-[44px] min-w-[44px] touch-manipulation items-center justify-center rounded-[14px] p-2 text-slate-700 transition-all duration-base ease-out hover:bg-slate-100"
            aria-label="Open menu"
            aria-expanded={sideMenuOpen}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        {/* TENSION Logo */}
        <div className="flex flex-1 items-center justify-center md:justify-start md:ml-6">
          <button
            onClick={() => handleNavigate(isEnterpriseRoute ? '/enterprise' : '/home')}
            className="group flex items-center"
            aria-label="Go to home"
          >
            {isEnterpriseRoute ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-700 to-slate-800">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-neutral-100">TENSION Enterprise</span>
              </div>
            ) : (
              <TensionLogo
                tone="dark"
                height={52}
                priority
                className="h-auto max-w-[210px] sm:max-w-[260px] lg:max-w-[300px]"
              />
            )}
          </button>
        </div>

        {/* Theme Toggle & User Info */}
        <div className="flex items-center gap-2">
          <PremiumValueIndicator />
          {user && (
            <button
              onClick={() => handleNavigate('/settings')}
              className="hidden items-center gap-2 rounded-[14px] px-3 py-2 transition-all duration-base ease-out hover:bg-slate-100 md:flex"
              aria-label="User settings"
            >
              <NavUserAvatar imageUrl={profilePhotoUrl} size="sm" />
              <div className="hidden text-left lg:block">
                <p className="text-sm font-medium text-slate-900">
                  {displayName}
                </p>
                <p className="text-xs font-semibold capitalize text-slate-700">
                  {displayPlan}
                </p>
              </div>
            </button>
          )}
        </div>
      </div>
    </motion.header>
  );

  // Mobile Bottom Navigation
  const MobileBottomNav = () => (
    <motion.nav
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-[rgba(255,255,255,0.05)] bg-[rgba(17,24,39,0.6)] shadow-[0_20px_40px_rgba(0,0,0,0.4)] backdrop-blur-[20px] md:hidden"
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-around px-2 py-2 safe-area-inset-bottom">
        {mainNavItems.slice(0, 4).map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          // Check if premium feature
          if (item.premium && !isPremium) {
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate('/pricing')}
                className="relative flex min-h-[44px] min-w-[44px] touch-manipulation flex-col items-center gap-1 rounded-lg px-3 py-2 transition-colors"
                aria-label={`${safeStr(item.label)} (Premium)`}
              >
                <div className="relative">
                  <Icon className="h-6 w-6 stroke-[1.75] text-[#64748B]" />
                  <Crown className="w-3 h-3 text-yellow-500 absolute -top-1 -right-1" />
                </div>
                <span className="text-xs text-[#64748B]">
                  {safeStr(item.label)}
                </span>
              </button>
            );
          }

          return (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              className={`relative flex min-h-[44px] min-w-[44px] touch-manipulation flex-col items-center gap-1 rounded-lg px-3 py-2 transition-colors ${
                active 
                  ? 'text-white' 
                  : 'text-[#64748B] hover:text-[#94A3B8]'
              }`}
              aria-label={safeStr(item.label)}
              aria-current={active ? 'page' : undefined}
            >
              <Icon className="h-6 w-6 stroke-[1.75]" />
              <span className="text-xs">
                {safeStr(item.label)}
              </span>
              {active && (
                <motion.div
                  layoutId="mobile-nav-indicator"
                  className="absolute -top-0.5 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-[linear-gradient(90deg,#22D3EE_0%,#8B5CF6_50%,#D946EF_100%)]"
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                />
              )}
            </button>
          );
        })}
      </div>
    </motion.nav>
  );

  // Side Drawer Menu (replaces desktop sidebar)
  const SideDrawer = () => (
    <AnimatePresence>
      {sideMenuOpen && (
        <Backdrop onClick={() => setSideMenuOpen(false)}>
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="fixed bottom-0 left-0 top-0 flex min-h-0 w-80 max-w-[85vw] flex-col overscroll-contain border-r border-[rgba(255,255,255,0.05)] bg-[rgba(17,24,39,0.92)] shadow-[0_20px_40px_rgba(0,0,0,0.4)] backdrop-blur-[20px]"
            role="dialog"
            aria-label="Navigation menu"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[rgba(255,255,255,0.05)] p-6">
              <div className="min-w-0">
                {isEnterpriseRoute ? (
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-700 to-slate-800">
                      <ShieldCheck className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-lg font-bold text-white">TENSION Enterprise</span>
                  </div>
                ) : <span className="text-lg font-semibold tracking-wide text-white">TENSION</span>}
                <p className="mt-1 text-xs text-[#94A3B8]">
                  {isEnterpriseRoute ? 'Workforce relationship intelligence' : 'Navigation'}
                </p>
              </div>
              <button
                onClick={() => setSideMenuOpen(false)}
                className="flex min-h-[44px] min-w-[44px] touch-manipulation items-center justify-center rounded-[14px] p-2 transition-all duration-base ease-out hover:bg-white/[0.04]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5 stroke-[1.75] text-[#94A3B8]" />
              </button>
            </div>

            {/* User Info */}
            {user && (
              <div className="border-b border-[rgba(255,255,255,0.05)] p-6">
                <div className="flex items-center gap-3">
                  <NavUserAvatar imageUrl={profilePhotoUrl} size="md" />
                  <div className="min-w-0">
                    <p className="font-medium text-white">
                      {displayName}
                    </p>
                    <p className="text-sm capitalize text-[#94A3B8]">
                      {displayPlan} Plan
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Items */}
            <nav className="p-4 space-y-6 flex-1 min-h-0 overflow-y-auto overscroll-contain">
              {/* Main Items */}
              <div className="space-y-1">
                <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-[#64748B]">
                  {isEnterpriseRoute ? 'Enterprise' : 'Main'}
                </p>
                {mainNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);

                  if (item.premium && !isPremium) {
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigate('/pricing')}
                        className="flex min-h-[44px] w-full touch-manipulation items-center gap-3 rounded-[14px] px-3 py-3 text-[#94A3B8] transition-all duration-base ease-out hover:bg-white/[0.04]"
                      >
                        <div className="relative">
                          <Icon className="w-5 h-5" />
                          <Crown className="w-3 h-3 text-yellow-500 absolute -top-1 -right-1" />
                        </div>
                        <span className="text-sm font-medium">{safeStr(item.label)}</span>
                        <span className="ml-auto text-xs bg-yellow-900/30 text-yellow-400 px-2 py-0.5 rounded-full">
                          Pro
                        </span>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigate(item.path)}
                      className={`flex min-h-[44px] w-full touch-manipulation items-center gap-3 rounded-[14px] px-3 py-3 transition-all duration-base ease-out ${
                        active
                          ? 'bg-white/[0.04] text-white shadow-[0_0_20px_rgba(139,92,246,0.35)]'
                          : 'text-[#94A3B8] hover:bg-white/[0.04]'
                      }`}
                    >
                      <Icon className="h-5 w-5 stroke-[1.75]" />
                      <span className="text-sm font-medium">{safeStr(item.label)}</span>
                    </button>
                  );
                })}
              </div>

              {/* Secondary Items */}
              <div className="space-y-1">
                <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-[#64748B]">
                  {isEnterpriseRoute ? 'Operations' : 'More'}
                </p>
                {secondaryNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);

                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigate(item.path)}
                      className={`flex min-h-[44px] w-full touch-manipulation items-center gap-3 rounded-[14px] px-3 py-2.5 transition-all duration-base ease-out ${
                        active
                          ? 'bg-white/[0.04] text-white shadow-[0_0_20px_rgba(139,92,246,0.35)]'
                          : item.highlight
                          ? 'text-[#22D3EE] hover:bg-white/[0.04]'
                          : 'text-[#94A3B8] hover:bg-white/[0.04]'
                      }`}
                    >
                      <Icon className="h-5 w-5 stroke-[1.75]" />
                      <span className="text-sm">{safeStr(item.label)}</span>
                      {item.highlight && <Briefcase className="w-4 h-4 ml-auto" />}
                    </button>
                  );
                })}
              </div>

              {/* Community Items */}
              {!isEnterpriseRoute && (
                <div className="space-y-1">
                  <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-[#64748B]">
                    Community
                  </p>
                  {communityNavItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigate(item.path)}
                        className={`flex min-h-[44px] w-full touch-manipulation items-center gap-3 rounded-[14px] px-3 py-2.5 transition-all duration-base ease-out ${
                          active
                            ? 'bg-white/[0.04] text-white shadow-[0_0_20px_rgba(139,92,246,0.35)]'
                            : 'text-[#94A3B8] hover:bg-white/[0.04]'
                        }`}
                      >
                        <Icon className="h-5 w-5 stroke-[1.75]" />
                        <span className="text-sm">{safeStr(item.label)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </nav>

            {user && signOut && (
              <div className="shrink-0 border-t border-[rgba(255,255,255,0.05)] bg-[rgba(17,24,39,0.6)] p-4">
                <button
                  type="button"
                  onClick={() => void handleSignOutFromMenu()}
                  className="flex min-h-[44px] w-full touch-manipulation items-center justify-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-red-400 transition-colors hover:bg-red-950/40"
                >
                  <LogOut className="w-5 h-5 shrink-0" />
                  Sign out
                </button>
              </div>
            )}
          </motion.div>
        </Backdrop>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* Top Header - shown on all screen sizes */}
      <TopHeader />
      
      {/* Side Drawer - shown on all screen sizes */}
      <SideDrawer />

      {/* Mobile Bottom Navigation - only on mobile */}
      {(variant === 'mobile' || variant === 'both') && (
        <MobileBottomNav />
      )}
    </>
  );
}

// Spacer component to prevent content from being hidden under bottom nav
export function NavigationSpacer() {
  return <div className="h-[calc(5rem+env(safe-area-inset-bottom))] md:h-0" aria-hidden="true" />;
}